/**
 * ConversationLogger — collects transcript messages and tool actions during
 * a widget conversation and POSTs the log to the logConversation Cloud Function
 * when the session ends.
 *
 * Matches the existing Holostaff `ConversationRequest` schema:
 *   { staffId, staffName, userId, startTime, lastMessageAt, messages, actions }
 */

const DEFAULT_BACKEND = 'https://us-central1-holostaff-ai.cloudfunctions.net'

export interface LoggedMessage {
  sender: 'user' | 'staff'
  text: string
  timestamp: string
}

export interface LoggedAction {
  tool: string
  args: Record<string, unknown>
  timestamp: string
}

export class ConversationLogger {
  private staffId: string
  private staffName: string
  private backendUrl: string

  private startTime: string
  private userId: string
  private messages: Record<string, LoggedMessage> = {}
  private actions: LoggedAction[] = []
  private msgCounter = 0

  constructor(opts: {
    staffId: string
    staffName: string
    backendUrl?: string
  }) {
    this.staffId = opts.staffId
    this.staffName = opts.staffName
    this.backendUrl = opts.backendUrl ?? DEFAULT_BACKEND
    this.startTime = new Date().toISOString()
    // Generate a pseudo-anonymous visitor ID (not PII)
    this.userId = `visitor_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`
  }

  /** Log a user transcript (from ASR). */
  addUserMessage(text: string): void {
    if (!text.trim()) return
    const key = `msg_${this.msgCounter++}`
    this.messages[key] = {
      sender: 'user',
      text: text.trim(),
      timestamp: new Date().toISOString(),
    }
  }

  /** Log an AI response text. */
  addStaffMessage(text: string): void {
    if (!text.trim()) return
    const key = `msg_${this.msgCounter++}`
    this.messages[key] = {
      sender: 'staff',
      text: text.trim(),
      timestamp: new Date().toISOString(),
    }
  }

  /** Log a tool call / page action. */
  addAction(tool: string, args: Record<string, unknown>): void {
    this.actions.push({
      tool,
      args,
      timestamp: new Date().toISOString(),
    })
  }

  /** Get a brief text summary of the conversation for the vision loop. */
  getSummary(): string {
    const msgs = Object.values(this.messages)
    if (msgs.length === 0) return 'No conversation took place.'

    // Build a brief transcript: "User: ... | Staff: ... | User: ..."
    const lines = msgs.slice(-6).map(m =>
      `${m.sender === 'user' ? 'User' : 'Agent'}: ${m.text.slice(0, 80)}`
    )
    return lines.join(' | ')
  }

  /** Send the conversation log to the backend. Fire-and-forget. */
  async flush(): Promise<void> {
    // Only log if there are actual messages
    if (Object.keys(this.messages).length === 0) {
      console.log('[Logger] No messages to log, skipping')
      return
    }

    const lastMsg = Object.values(this.messages).pop()
    const payload = {
      staffId: this.staffId,
      staffName: this.staffName,
      userId: this.userId,
      startTime: this.startTime,
      lastMessageAt: lastMsg?.timestamp ?? this.startTime,
      messages: this.messages,
      // Extra metadata — the Cloud Function will pass these through to RTDB
      actions: this.actions,
      source: 'widget_v2',
    }

    console.log('[Logger] Flushing conversation log:', {
      messages: Object.keys(this.messages).length,
      actions: this.actions.length,
    })

    try {
      const res = await fetch(`${this.backendUrl}/logConversation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })

      if (res.ok) {
        const data = await res.json()
        console.log('[Logger] Conversation logged:', data.id)
      } else {
        console.warn('[Logger] Failed to log conversation:', res.status)
      }
    } catch (err) {
      // Don't let logging failures affect the user experience
      console.warn('[Logger] Error logging conversation:', err)
    }
  }
}
